import { ScreenCue } from "./ScreenCue";
import { PixelIcon } from "./PixelArt";
import { createPortal } from "preact/compat";
import type { ComponentChildren } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { api } from "../api";
import { prepareImage } from "../image";
import { Lightbox } from "./Lightbox";
import { beginOperation, endOperation, measure, report } from "../telemetry";
import { beginPhotoPick, endPhotoPick, markPhotoStage, type PhotoSource } from "../photo-pick";
import { nativePlatform } from "./InstallHint";
import { useSheetLayer } from "../devsheet-hooks";
import { useExit } from "./useExit";
import { BusyLabel } from "./BusyLabel";

// Inside the Android app photos come through the Camera plugin (src/native-camera.ts), which
// survives Android killing the app behind the camera; browsers and the iOS app keep the file
// inputs. A dynamic import, so nothing else ever downloads the plugin.
const nativeCamera = () => import("../native-camera");

interface Props {
  label: string;
  photoId: string | null;
  onChange: (photoId: string | null) => void;
  /** Called after the peon confirms a freshly taken photo, with its id. Use it to submit immediately. */
  onConfirm?: (photoId: string) => void;
  /** Called when the peon triggers the camera or file chooser, before anything is picked. */
  onStart?: () => void;
  /** Called when the "Looks good?" check is dismissed without confirming (Cancel, or leaving mid-flow). */
  onCancel?: () => void;
  /** Text on the confirm button of the "Looks good?" check. */
  confirmLabel?: string;
  /**
   * "field" only: a second way out of the "Looks good?" check that keeps the photo in the form and
   * skips `onConfirm` ("Keep editing" under Add task). Unset, the check has its one confirm.
   */
  keepLabel?: string;
  /** Extra controls shown on the "Looks good?" check, above the confirm button (Add Task's Urgent! and Big job). */
  checkExtras?: ComponentChildren;
  /**
   * "field": preview box with camera/library actions (forms).
   * "button": a single primary button that opens the phone's own chooser, camera or library (task
   *   completion; 2026-10-02 ruling in docs/product.md). The photo takes the same "Looks good?" check.
   * "quiet": a ghost button (`label`, "Add a photo") that opens the same chooser, and once a photo is
   *   in, a small instant photo of it (a tap shows it full screen) with "Remove photo" beside it (the
   *   note composer, docs/notes.md "Photo notes").
   */
  variant?: "field" | "button" | "quiet";
  allowClear?: boolean;
  /** "button" only: the confirmed photo's request is running; the button reads "Working…" and waits. */
  working?: boolean;
  /**
   * "button" only: when set, a press runs this instead of opening the camera. Job's Done uses it to
   * send the photo already taken again after the completion failed.
   */
  onPress?: () => void;
  /** "button" only: the button's tour target name (`data-tour`), for the tour's spotlight (src/tour-targets.ts). */
  tour?: string;
  /** "button" only: "ghost" draws the ghost button (a project's Add progress, docs/projects.md) instead of the gold primary. */
  tone?: "primary" | "ghost";
  /**
   * What the photo is for, when another picker shares this route (a project's Add progress beside Job's
   * Done, shared/projects.ts PROGRESS_PICK): a photo the Android app gets back after a restart comes
   * only to the picker with the same key. Unset for every other picker.
   */
  restoreKey?: string;
  /** "quiet" only: the words on the button that takes the photo off again. */
  clearLabel?: string;
}

/**
 * Camera-first photo input. The photo is downscaled on the device and uploaded
 * while a full-screen "Looks good?" check is shown; only when the peon confirms
 * does the id reach the form (and `onConfirm` fires), so nothing needs to be
 * scrolled to afterwards.
 */
export function PhotoPicker({ label, photoId, onChange, onConfirm, onStart, onCancel, confirmLabel = "Use photo", keepLabel, checkExtras, variant = "field", allowClear = true, working = false, onPress, tour, clearLabel = "Remove photo", tone = "primary", restoreKey }: Props) {
  // Only a picker with a restore key passes it: every other one starts and takes its picks as before.
  const beginPick = (kind: PhotoSource) => (restoreKey ? beginPhotoPick(kind, restoreKey) : beginPhotoPick(kind));
  const cameraRef = useRef<HTMLInputElement>(null);
  const libraryRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [zoomed, setZoomed] = useState(false);
  // The photo awaiting confirmation: local preview URL, and the uploaded id once it lands.
  const [pending, setPending] = useState<{ url: string; id: string | null } | null>(null);
  // Mirrors `pending` and whether it was confirmed, so the unmount cleanup below can tell a
  // flow that closed mid-check (navigating away) from one that finished normally.
  const pendingRef = useRef<{ url: string; id: string | null } | null>(null);
  const confirmedRef = useRef(false);
  pendingRef.current = pending;

  useEffect(() => () => {
    if (preview) URL.revokeObjectURL(preview);
  }, [preview]);

  // Leaving the screen while a photo is awaiting confirmation counts as abandoning the flow.
  useEffect(
    () => () => {
      if (pendingRef.current && !confirmedRef.current) onCancel?.();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  async function handleFile(file: File | undefined) {
    if (!file) return;
    setError(null);
    setBusy(true);
    beginOperation("photo", { inBytes: file.size, inType: file.type });
    let full: Blob, thumb: Blob, path: "bitmap" | "img";
    const prepStart = performance.now();
    try {
      ({ full, thumb, path } = await prepareImage(file));
      markPhotoStage("prepared");
    } catch (e) {
      report("photo.prepare.failed", e instanceof Error ? e.message : "Could not read photo", {
        inBytes: file.size,
        inType: file.type,
        ms: Math.round(performance.now() - prepStart),
      });
      endOperation();
      endPhotoPick();
      setBusy(false);
      setError(e instanceof Error ? e.message : "Could not read photo");
      return;
    }
    const deviceMemory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
    report("photo.prepare", "", {
      inBytes: file.size,
      inType: file.type,
      fullBytes: full.size,
      thumbBytes: thumb.size,
      ms: Math.round(performance.now() - prepStart),
      path,
      deviceMemory,
    });
    const url = URL.createObjectURL(full);
    setPending({ url, id: null });
    markPhotoStage("check");
    try {
      const { id } = await measure("photo.upload", { bytes: full.size }, () => api.photos.upload(full));
      markPhotoStage("uploaded");
      setPending((p) => (p && p.url === url ? { url, id } : p));
      // Fire-and-forget: the thumb must never delay the "Looks good?" check or
      // the confirm/navigate flow. A failed thumb upload just leaves the server
      // falling back to the full image for that photo.
      void measure("photo.thumb_upload", { bytes: thumb.size }, () => api.photos.uploadThumb(id, thumb)).catch((e) =>
        console.warn("Thumb upload failed", e),
      );
    } catch (e) {
      endPhotoPick();
      setError(e instanceof Error ? e.message : "Upload failed");
      setPending(null);
      URL.revokeObjectURL(url);
    } finally {
      setBusy(false);
      endOperation();
    }
  }

  // `submit` false is "Keep editing": the photo goes into the form and the check leaves, but the
  // form's onConfirm (which posts it) is not called.
  function confirm(submit = true) {
    if (!pending?.id) return;
    markPhotoStage("confirmed");
    confirmedRef.current = true;
    if (preview) URL.revokeObjectURL(preview);
    setPreview(pending.url);
    onChange(pending.id);
    const id = pending.id;
    if (submit) onConfirm?.(id);
    // The request is already on its way; the check steps out over it and then goes.
    checkExit.close("confirm");
    // Give the completion request a moment, then consider the photo delivered. Kept for editing,
    // it is delivered now.
    if (submit) setTimeout(endPhotoPick, 4000);
    else endPhotoPick();
  }

  function retake() {
    if (pending) URL.revokeObjectURL(pending.url);
    setPending(null);
    onStart?.();
    openCamera();
  }

  function discardPending() {
    endPhotoPick();
    checkExit.close("discard");
  }
  // The check leaves the way it came (src/components/useExit.ts); it goes once the exit has played.
  // A screen left mid-exit never gets here, and the unmount cleanup above reports the cancel instead.
  const checkExit = useExit<[kind: "confirm" | "discard"]>((kind) => {
    const p = pendingRef.current;
    if (kind === "discard" && p) URL.revokeObjectURL(p.url);
    setPending(null);
    if (kind === "discard") onCancel?.();
  });
  // Escape on the photo check is its Cancel.
  const checkRef = useSheetLayer(pending !== null && !checkExit.closing, discardPending);

  // A photo the Android app got back after Android killed it behind the camera
  // (src/native-camera.ts): the app reopens on this route and the picker carries on with it as if
  // the camera had just handed it over. It may arrive before this mounts or just after.
  useEffect(() => {
    if (nativePlatform() !== "android") return;
    let stop: (() => void) | null = null;
    let unmounted = false;
    void nativeCamera().then((m) => {
      if (unmounted) return;
      const take = () => {
        const file = restoreKey ? m.takeRestoredPhoto(location.hash, restoreKey) : m.takeRestoredPhoto(location.hash);
        if (file) void handleFile(file);
      };
      stop = m.onRestoredPhoto(take);
      take();
    });
    return () => {
      unmounted = true;
      stop?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The Android app's pick: the system camera or photo picker through the Camera plugin. Backing
  // out ends the pick quietly, like cancelling a file input.
  async function pickNative(kind: PhotoSource) {
    try {
      const file = await (await nativeCamera()).pickNativePhoto(kind);
      if (!file) {
        endPhotoPick();
        return;
      }
      markPhotoStage("file");
      await handleFile(file);
    } catch (e) {
      endPhotoPick();
      setError(e instanceof Error ? e.message : "Could not read photo");
    }
  }

  // Hand-offs to the camera or library silence the peon first and record the start, so a photo
  // lost to Android killing the app while the camera is open shows up as photo.lost (photo-pick.ts).
  function openCamera() {
    beginPick("camera");
    if (nativePlatform() === "android") void pickNative("camera");
    else cameraRef.current?.click();
  }
  function openLibrary() {
    beginPick("library");
    if (nativePlatform() === "android") void pickNative("library");
    else libraryRef.current?.click();
  }
  // Job's Done: the phone decides. The library input has no `capture`, so iOS offers "Take Photo"
  // and "Photo Library" and Android its camera-or-files chooser; the Android app asks the Camera
  // plugin for its prompt. Retake from the check still goes straight to the camera.
  function openChooser() {
    beginPick("either");
    if (nativePlatform() === "android") void pickNative("either");
    else libraryRef.current?.click();
  }

  const src = preview ?? (photoId ? api.photos.url(photoId) : null);

  const inputs = (
    <>
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onCancel={endPhotoPick}
        onChange={(e) => {
          if (e.currentTarget.files?.[0]) markPhotoStage("file");
          else endPhotoPick();
          void handleFile(e.currentTarget.files?.[0]);
          e.currentTarget.value = "";
        }}
      />
      <input
        ref={libraryRef}
        type="file"
        accept="image/*"
        hidden
        onCancel={endPhotoPick}
        onChange={(e) => {
          if (e.currentTarget.files?.[0]) markPhotoStage("file");
          else endPhotoPick();
          void handleFile(e.currentTarget.files?.[0]);
          e.currentTarget.value = "";
        }}
      />
    </>
  );

  // Rendered at the body, never inside the picker's parent: the check is position: fixed, and
  // an ancestor with a filter or transform (a task card's :active brightness, for one) would
  // become its containing block, collapsing it into the card the instant a finger lands on
  // the confirm button. The press then ends over something else and no click is delivered.
  const check = pending && createPortal(
    <div class={`photo-check${checkExit.cls}`} ref={(el) => { checkRef.current = el; checkExit.ref.current = el; }} role="dialog" aria-modal="true" tabIndex={-1} aria-label="Check the photo" data-peon-quiet>
      <ScreenCue closing={checkExit.closing} />
      <div class="photo-check-stage">
        <img src={pending.url} alt="" />
      </div>
      <div class="photo-check-bar">
        <div class="photo-check-title" role="status">{pending.id ? "Looks good?" : "Uploading…"}</div>
        {checkExtras && <div class="photo-check-extras">{checkExtras}</div>}
        <div class={`photo-check-actions${variant === "field" && keepLabel ? " has-keep" : ""}`}>
          {/* Job's Done and the note composer keep Retake here; on a form the frame's own retake icon covers it. */}
          {variant !== "field" && (
            <button type="button" class="btn btn-ghost" onClick={retake}>
              <PixelIcon name="retake" /> Retake
            </button>
          )}
          <button type="button" class="btn btn-primary" onClick={() => confirm()} disabled={!pending.id}>
            {confirmLabel}
          </button>
          {variant === "field" && keepLabel && (
            <button type="button" class="btn btn-ghost photo-check-keep" onClick={() => confirm(false)} disabled={!pending.id}>
              {keepLabel}
            </button>
          )}
        </div>
        <button type="button" class="btn btn-ghost small-btn" onClick={discardPending}>
          Cancel
        </button>
      </div>
    </div>,
    document.body,
  );

  if (variant === "quiet") {
    return (
      <div class="photo-picker photo-quiet" data-peon-quiet>
        {src ? (
          <div class="photo-quiet-row">
            <button type="button" class="photo-quiet-thumb" onClick={() => !busy && setZoomed(true)} aria-label="View the photo" disabled={busy}>
              <img src={preview ?? (photoId ? api.photos.thumbUrl(photoId) : src)} alt="" />
            </button>
            {zoomed && <Lightbox src={src} alt="The photo" onClose={() => setZoomed(false)} />}
            {allowClear && (
              <button
                type="button"
                class="btn-quiet photo-quiet-remove"
                onClick={() => {
                  if (preview) URL.revokeObjectURL(preview);
                  setPreview(null);
                  onChange(null);
                }}
                disabled={busy}
              >
                {clearLabel}
              </button>
            )}
          </div>
        ) : (
          <button type="button" class="btn btn-ghost photo-quiet-add" onClick={() => { onStart?.(); openChooser(); }} disabled={busy}>
            <PixelIcon name="camera" /> <BusyLabel busy={busy}>{label}</BusyLabel>
          </button>
        )}
        {error && <div class="form-error" role="alert">{error}</div>}
        {inputs}
        {check}
      </div>
    );
  }

  if (variant === "button") {
    return (
      <div class="photo-picker" data-peon-quiet>
        <button
          type="button"
          class={`btn ${tone === "ghost" ? "btn-ghost" : "btn-primary"} btn-block`}
          data-tour={tour}
          onClick={() => {
            if (onPress) {
              onPress();
              return;
            }
            onStart?.();
            openChooser();
          }}
          disabled={busy || working}
        >
          <PixelIcon name="camera" /> <BusyLabel busy={busy || working}>{label}</BusyLabel>
        </button>
        {error && <div class="form-error" role="alert">{error}</div>}
        {inputs}
        {check}
      </div>
    );
  }

  return (
    <div class="photo-picker" data-peon-quiet>
      <div class="field-label">{label}</div>
      {/* The frame carries its own controls in the corners (44px targets): retake bottom-left when
          there is a photo, the library bottom-right always. No separate button row. */}
      <div class="photo-stage">
        {src ? (
          <div class="photo-frame">
            <img src={src} alt={label} role="button" tabIndex={0} aria-label={`View ${label.toLowerCase()}`} onClick={() => !busy && setZoomed(true)} onKeyDown={(e) => {
              if (!busy && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                setZoomed(true);
              }
            }} />
            {zoomed && <Lightbox src={src} alt={label} onClose={() => setZoomed(false)} />}
          </div>
        ) : (
          <button type="button" class="photo-placeholder" onClick={() => openCamera()} disabled={busy}>
            <PixelIcon name="camera" className="photo-placeholder-icon" />
            <span><BusyLabel busy={busy}>Take a photo</BusyLabel></span>
          </button>
        )}
        {src && (
          <button type="button" class="photo-corner photo-corner-left" onClick={() => openCamera()} disabled={busy} aria-label="Retake photo">
            <PixelIcon name="retake" />
            <span class="photo-corner-label">Retake</span>
          </button>
        )}
        <button type="button" class="photo-corner photo-corner-right" onClick={() => openLibrary()} disabled={busy} aria-label="Choose from library">
          <PixelIcon name="library" />
          <span class="photo-corner-label">Library</span>
        </button>
      </div>
      {src && allowClear && (
        <button
          type="button"
          class="link-btn danger photo-remove"
          onClick={() => {
            if (preview) URL.revokeObjectURL(preview);
            setPreview(null);
            onChange(null);
          }}
          disabled={busy}
        >
          Remove photo
        </button>
      )}
      {error && <div class="form-error" role="alert">{error}</div>}
      {inputs}
      {check}
    </div>
  );
}
